import { CommonButton, CommonDropdownOutlined, OutlinedTextFields, Popup } from '@Components';
import { API_URL } from '@Utils/config';
import {
  auMobValidation,
  dynamicMobileValidation,
  minLength10,
  normalizeAll,
  r_maxLength,
  r_onlyNumbers,
  required
} from '@Utils/validation';
import React, { useCallback, useEffect, useState } from 'react';
import { change, Field, getFormValues, reduxForm, reset } from 'redux-form';
import {
  CountryList,
  GenericRecordType,
  PersonalDetailsFormValueType,
  PersonalDetailsPropsType
} from '../utils/types';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IStore } from '../../../../../store.type';
import { FieldContainer } from '../../receiving-method/styles';
import NameField from './NameField';
import { fetchDropdownCountries } from '@Redux/actions/individual-user-flow';
import { handleError } from '@Redux/actions/common-message';
import {
  BENE_ADDRESS_HELPER_TEXT,
  INR_BENE_CURRENCY_MSG,
  KENYA_WALLET_MSG,
  VALID_CHINA_NO_MSG
} from '../utils/constants';
import { BeneIdentityHelperText, MobileBox, MobileCode, MobileNumber, WalletText } from '../styles';
import { loadMobileValidation } from '@Redux/actions/otp-request/otp-request-action';
import { compact, isEmpty } from 'lodash';
import { CORPORATE, INDIVIDUAL, MANUAL_ADDRESS_FORM } from '@Utils/constants';
import { BENE_IDENTITY_HELPER_TEXT } from '../../../../bene-info-correction/utils';
import { Col } from '../../../../nium-forex/styles';
import {
  getMobileValidationReducer,
  selectNewBeneState,
  selectUserSessionReducer,
  selectUserStatusReducer
} from '@Redux/selectors';
import PersonalScreen from '../../../../beneficiary-info/personal-info';
import { setBeneRevampFormValues } from '@Redux/actions/new-bene-flow';
import { screenNames } from '../../../constants';
import { BENE_PERSONAL_DETAILS_FORM_NAME } from '../../../constants/commons';
import {
  navigateHistory,
  setCurrentBeneDataAction,
  setCurrentFormBeneAddressAction
} from '@Redux/actions/new-bene-flow/new-bene-flow-action';
import { ADDRESS_ARRAY } from '../../../../bene-additional-info/utils/constants';
import { dispatchConstFields } from '../../../../beneficiary-info/common';
import { scrollToError, scrollToTop } from '@Redux/actions/ui-trigger';
import {
  selfPopulatingDataBeneSchemaFields,
  selfPopulatingDataNonBeneSchema,
  toValidateEmail
} from '../utils/methods';
import { makeUrl } from '@Utils/library';
import { BankNameContainer } from '../../../../beneficiary-info/styled-components';
import { INFO_GREY_ICON } from '@Images';
import { BANK_NAME_POPUP_CONTENT } from '../../../../../components/alert-Message/constants';

const PersonalDetailsForm: React.FC<PersonalDetailsPropsType> = (props) => {
  const {
    handleSubmit,
    initialize,
    parentState: { beneState, isEditFlow }
  } = props;

  const dispatch = useDispatch<AppDispatch>();

  const [beneCountryFieldDisabled, setBeneCountryFieldDisabled] = useState<boolean>(false);
  const [beneIdTypeFieldDisabled, setBeneIdTypeFieldDisabled] = useState<boolean>(false);
  const [countryDropdown, setCountryDropdown] = useState<CountryList>([]);
  const [mobCode, setMobCode] = useState<string>('');
  const [mobileValidation, setMobileValidation] = useState<GenericRecordType | null>(null);

  const userStatusReducer = useSelector(selectUserStatusReducer);
  const newBeneStateReducer = useSelector(selectNewBeneState);
  const { length: mobileValidationLength } = useSelector(getMobileValidationReducer) || {};

  const {
    isBeneSchemaEnabled: isBeneSchemaApplicable,
    schemaApiResponse,
    selectedCountryMobileCode,
    formValues: { details_selected, account_type, bank_account_country, currency_code },
    currentBene,
    beneficiaryBankName
  } = newBeneStateReducer;

  const { country_code: remitter_country } =
    useSelector(selectUserSessionReducer)?.user_session || {};

  const formValues = useSelector((state: IStore) =>
    getFormValues(BENE_PERSONAL_DETAILS_FORM_NAME)(state)
  ) as PersonalDetailsFormValueType;

  const combinedFormValues = {
    ...newBeneStateReducer?.formValues,
    ...formValues
  };

  const { remitter_beneficiary_relationship, country_code, mobile_country_code } = formValues || {};
  const { is_partially_editable, is_completely_editable } = window.history.state?.usr || {};
  const disableMobileCountryCode = ['KE'].includes(bank_account_country);
  const isUSUSDBene = bank_account_country === 'US' && currency_code === 'USD';
  const isOnlyPartiallyEditable = is_partially_editable && !is_completely_editable;
  const isCountryOfResidenceDisabled =
    isOnlyPartiallyEditable || remitter_country === 'AU' || bank_account_country === 'AU';

  const checkRelationType = (obj: GenericRecordType) => {
    selfPopulatingDataNonBeneSchema(
      obj.text === 'Self',
      dispatch,
      combinedFormValues,
      countryDropdown,
      userStatusReducer,
      setMobileValidation,
      account_type,
      mobCode
    );
  };

  const validateMobile =
    mobile_country_code === '61'
      ? compact([!isUSUSDBene ? required : null, auMobValidation, dynamicMobileValidation])
      : compact([!isUSUSDBene ? required : null, dynamicMobileValidation]);

  const selfPopulatingDataSchemaFields = (data: GenericRecordType) => {
    selfPopulatingDataBeneSchemaFields(
      data,
      dispatch,
      schemaApiResponse,
      userStatusReducer,
      beneCountryFieldDisabled,
      beneIdTypeFieldDisabled,
      bank_account_country
    );
  };

  const getCountryDropdownValues = useCallback(async () => {
    try {
      const data = await dispatch(fetchDropdownCountries({ url: `${API_URL}/countries/dropdown` }));
      setCountryDropdown(data);
    } catch (error) {
      dispatch(handleError(error));
    }
  }, [dispatch]);

  useEffect(() => {
    getCountryDropdownValues();
  }, [getCountryDropdownValues]);

  // Pre-populate country of residence from destination country for all benes on mount.
  // Waits for countryDropdown to be loaded so the dropdown can resolve and display the matching label.
  useEffect(() => {
    if (!isEditFlow && bank_account_country && countryDropdown.length > 0) {
      dispatch(change(BENE_PERSONAL_DETAILS_FORM_NAME, 'country_code', bank_account_country));
      dispatch(
        change(BENE_PERSONAL_DETAILS_FORM_NAME, 'beneficiary_country_code', bank_account_country)
      );
    }
  }, [bank_account_country, isEditFlow, dispatch, countryDropdown]);

  // Clear saved address when user changes country of residence in edit flow
  const onCountryOfResidenceChange = () => {
    if (isEditFlow) {
      dispatch(reset(MANUAL_ADDRESS_FORM));
      dispatch(setCurrentFormBeneAddressAction({}));
      // Strip address fields from currentBene so prefillBeneAddress doesn't re-populate
      // when the user navigates to the address step after changing country
      const updatedBene = { ...currentBene };
      ADDRESS_ARRAY.forEach((key) => {
        delete updatedBene[key];
      });
      dispatch(setCurrentBeneDataAction(updatedBene));
    }
  };

  useEffect(() => {
    if (isEditFlow && !isEmpty(currentBene)) {
      initialize({
        ...currentBene,
        beneficiary_name: currentBene?.full_name || currentBene?.beneficiary_name || '',
        beneficiary_country_code:
          currentBene?.country_code || currentBene?.beneficiary_country_code || ''
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditFlow, currentBene]);

  useEffect(() => {
    if (!isEmpty(schemaApiResponse)) {
      const response = schemaApiResponse;
      !isEmpty(response.payout_details) &&
        Object.keys(response.payout_details).forEach((field) => {
          if (!isEmpty(response.payout_details[field].render_web)) {
            response.payout_details[field].render = response.payout_details[field].render_web;
          }
        });
      dispatchConstFields({
        data: response,
        dispatch,
        change,
        formName: BENE_PERSONAL_DETAILS_FORM_NAME
      });
    } else {
      // Prefilling mobile country code for non bene schema countries as per recipient country
      if (selectedCountryMobileCode)
        dispatch(
          change(BENE_PERSONAL_DETAILS_FORM_NAME, 'mobile_country_code', selectedCountryMobileCode)
        );
      setMobCode('+' + selectedCountryMobileCode);
    }
  }, [schemaApiResponse, dispatch, selectedCountryMobileCode]);

  const mobileCountryCodeChange = (obj: GenericRecordType) => {
    if (obj) {
      setMobCode('+' + obj.code);
      setMobileValidation(obj.phone_number_validation?.length);
      dispatch(change(BENE_PERSONAL_DETAILS_FORM_NAME, 'mobile_number', ''));
      dispatch(loadMobileValidation(obj.phone_number_validation));
    }
  };

  const isIndividualChinaBeneWithCNYCurrency =
    account_type === INDIVIDUAL && bank_account_country === 'CN' && currency_code === 'CNY';
  const isINRBeniCurrency = currency_code === 'INR';
  const is_CO_or_KR_bene = country_code === 'KR' || country_code === 'CO';
  const isPhpCurrency = currency_code === 'PHP';
  const isRemitterUS = remitter_country === 'US';
  const isPolandEURBene = bank_account_country === 'PL' && currency_code === 'EUR';

  const isWallet = beneState?.beneWalletDetails?.routing_config_type === 'WALLET';

  const validateEmail = toValidateEmail(
    remitter_country,
    country_code,
    bank_account_country,
    currency_code,
    isWallet,
    isIndividualChinaBeneWithCNYCurrency,
    isPhpCurrency
  );

  const submitForm = () => {
    if (isEditFlow) {
      // Replacing the current beneficiary data with updated data for the form
      dispatch(setCurrentBeneDataAction({ ...currentBene, ...combinedFormValues }));
    }
    dispatch(setBeneRevampFormValues(combinedFormValues));
    dispatch(
      navigateHistory({
        type: 'PUSH_NAVIGATION_HISTORY',
        screen: screenNames.bank_details.name
      })
    );
    dispatch(scrollToTop());
  };

  const renderNonBeneCommonFields = () => {
    return (
      <>
        <FieldContainer>
          {beneficiaryBankName && (
            <BankNameContainer>
              <span className="bankingName">
                <p className="bankNameText">Banking name </p>
                <Popup
                  className="bankNamePopup"
                  hideOnScroll
                  position="top center"
                  trigger={<img alt="copy details" src={INFO_GREY_ICON} className="infoIcon" />}
                  on="click"
                  content={BANK_NAME_POPUP_CONTENT}
                />
                -
              </span>
              <p className="name">{` ${beneficiaryBankName}`}</p>
            </BankNameContainer>
          )}
          <NameField
            account_type={account_type}
            bank_account_country={bank_account_country}
            currency_code={currency_code}
            is_partially_editable={is_partially_editable}
            is_completely_editable={is_completely_editable}
          />
        </FieldContainer>
        <FieldContainer>
          <MobileBox>
            <MobileCode>
              <Field
                bindToThisObject={mobile_country_code}
                dependedOnObject={country_code} //Country of residence
                name="mobile_country_code"
                width="90px"
                type="text"
                boxSize={'small'}
                validate={[required]}
                component={CommonDropdownOutlined}
                disabled={disableMobileCountryCode || isOnlyPartiallyEditable}
                placeholder="Code"
                onDropdownClose={mobileCountryCodeChange}
                url={API_URL + '/country/phone-code'}
                selectedLabel={mobCode}
              />
            </MobileCode>
            <MobileNumber>
              <Field
                name="mobile_number"
                type="text"
                component={OutlinedTextFields}
                validate={validateMobile}
                disabled={isOnlyPartiallyEditable}
                normalize={(() => {
                  let restrictions = [r_onlyNumbers];
                  if (mobileValidation) {
                    if (mobileValidation.type === 'startsWithFour')
                      restrictions.push(r_onlyNumbers);
                    if (mobileValidation.length) {
                      let { max, exact } = mobileValidation.length;
                      restrictions.push(r_maxLength(exact || max));
                    }
                    if (mobileValidation) {
                      let { max, exact } = mobileValidation || mobileValidationLength || {};
                      restrictions.push(r_maxLength(exact || max || 50));
                    }
                  }

                  return normalizeAll(restrictions);
                })()}
                tooltip={
                  (isIndividualChinaBeneWithCNYCurrency && VALID_CHINA_NO_MSG) ||
                  (isINRBeniCurrency && INR_BENE_CURRENCY_MSG)
                }
                placeholder={isUSUSDBene ? 'Mobile number (optional)' : 'Mobile number'}
              />
            </MobileNumber>
          </MobileBox>
        </FieldContainer>
        <FieldContainer>
          {currency_code === 'KES' && details_selected === 'wallet' && (
            <WalletText>{KENYA_WALLET_MSG}</WalletText>
          )}
        </FieldContainer>
        <FieldContainer>
          {bank_account_country && (
            <Col width={450}>
              <Field
                name="email"
                type="text"
                component={OutlinedTextFields}
                normalize={r_maxLength(50)}
                {...validateEmail}
                placeholder={
                  (isRemitterUS &&
                    !is_CO_or_KR_bene &&
                    !isIndividualChinaBeneWithCNYCurrency &&
                    !isPhpCurrency) ||
                  isUSUSDBene ||
                  isWallet
                    ? 'Email (Optional)'
                    : 'Email'
                }
                maxWidth="100%"
                disabled={isOnlyPartiallyEditable}
              />
            </Col>
          )}
        </FieldContainer>
        {isPolandEURBene && (
          <FieldContainer>
            <div className={`identityNumberWrapper ${account_type === CORPORATE && 'noTopMargin'}`}>
              <Field
                name="beneficiary_identity_number"
                type="text"
                component={OutlinedTextFields}
                normalize={r_maxLength(18)}
                validate={[required, minLength10]}
                placeholder="Identity number"
                width={450}
                maxWidth="100%"
                disabled={isOnlyPartiallyEditable}
              />
              <div className="helperText">{BENE_IDENTITY_HELPER_TEXT}</div>
            </div>
          </FieldContainer>
        )}
        <FieldContainer>
          <Col width={450}>
            <Field
              bindToThisObject={country_code}
              name="country_code"
              type="text"
              component={CommonDropdownOutlined}
              placeholder="Country of residence"
              selectOnClose={true}
              validate={[required]}
              className="theme-dropdown"
              staticValues={countryDropdown}
              width={450}
              disabled={isCountryOfResidenceDisabled}
              onDropdownChange={onCountryOfResidenceChange}
            />
            <BeneIdentityHelperText>{BENE_ADDRESS_HELPER_TEXT}</BeneIdentityHelperText>
          </Col>
        </FieldContainer>
      </>
    );
  };

  const renderNonBeneSchemaIndividualFields = () => {
    return (
      <>
        <FieldContainer>
          <Col width={450}>
            <Field
              dependedOnObject={account_type}
              bindToThisObject={remitter_beneficiary_relationship}
              disabled={isOnlyPartiallyEditable}
              name="remitter_beneficiary_relationship"
              type="text"
              changeOptionType={true}
              component={CommonDropdownOutlined}
              placeholder="Relationship to you"
              validate={isOnlyPartiallyEditable ? [] : [required]}
              onDropdownChange={(obj: GenericRecordType) => checkRelationType(obj)}
              url={makeUrl(`${API_URL}/instarem-objects/dropdown`, {
                type: 'REMITTER_BENEFICIARY_RELATIONSHIP',
                account_type,
                country_code: remitter_country
              })}
              width={450}
            />
          </Col>
        </FieldContainer>
        {renderNonBeneCommonFields()}
      </>
    );
  };

  const renderNonBeneSchemaCorporateFields = () => {
    return (
      <>
        <FieldContainer>
          <Col width={450}>
            <Field
              dependedOnObject={account_type}
              bindToThisObject={remitter_beneficiary_relationship}
              disabled={isOnlyPartiallyEditable}
              name="remitter_beneficiary_relationship"
              type="text"
              component={CommonDropdownOutlined}
              placeholder="Sending money to"
              validate={isOnlyPartiallyEditable ? [] : [required]}
              onDropdownChange={(obj: GenericRecordType) => !isEditFlow && checkRelationType(obj)}
              url={makeUrl(`${API_URL}/instarem-objects/dropdown`, {
                type: 'REMITTER_BENEFICIARY_RELATIONSHIP',
                account_type,
                country_code: remitter_country
              })}
              width={450}
            />
            {/* Render banking name here for displayBeneName */}
          </Col>
        </FieldContainer>
        {renderNonBeneCommonFields()}
      </>
    );
  };

  return (
    <div className="child-component-container__content-container">
      <form onSubmit={handleSubmit(submitForm)}>
        {/* Non Bene schema Personal details fields */}
        {!isBeneSchemaApplicable ? (
          account_type === INDIVIDUAL ? (
            renderNonBeneSchemaIndividualFields()
          ) : (
            renderNonBeneSchemaCorporateFields()
          )
        ) : !isEmpty(schemaApiResponse?.personal_details) ? (
          // Bene Schema Personal details fields
          <Col width={450}>
            <PersonalScreen
              personal_fields={schemaApiResponse?.personal_details}
              dispatchAction={dispatch}
              changeAction={change}
              formValues={combinedFormValues}
              selfPopulatingData={selfPopulatingDataSchemaFields}
              isPartiallyEditable={isOnlyPartiallyEditable}
              beneficiaryBankName={beneficiaryBankName}
              isSingleRowField={true}
              isBeneAdditionFlow={true}
              applyInFieldValidation={true}
              formName={BENE_PERSONAL_DETAILS_FORM_NAME}
              setBeneCountryFieldDisabled={setBeneCountryFieldDisabled}
              setBeneIdTypeFieldDisabled={setBeneIdTypeFieldDisabled}
              isCountryOfResidenceDisabled={isCountryOfResidenceDisabled}
            />
          </Col>
        ) : null}

        <CommonButton
          margin={isBeneSchemaApplicable ? '16px 0 0 0' : '48px 0 0 0'}
          text="Next"
          width={450}
        />
      </form>
    </div>
  );
};

const PersonalDetails = reduxForm({
  form: BENE_PERSONAL_DETAILS_FORM_NAME,
  onSubmitFail: scrollToError,
  destroyOnUnmount: false
})(PersonalDetailsForm);

export default PersonalDetails;
